/**
 * Real-world wind from Open-Meteo, for the licensed real-area (Antares / BSB)
 * charts. We fetch an HOURLY 10 m wind forecast over the visible extent and keep
 * the WHOLE field (every point, every hour), so:
 *   - the barb overlay can show any forecast hour instantly, re-derived from the
 *     cached field rather than refetching;
 *   - a time-varying wind lookup `windAtTime(field, pos, ms)` is available for the
 *     auto-router to read the wind at the moment it reaches each point.
 *
 * Open-Meteo is JSON (no GRIB decoding) and CORS-friendly. Models: GFS (NOAA),
 * ECMWF, ICON (DWD). Real-area only; the fictional training area stays synthetic.
 * Data is CC-BY-4.0 (attribution shown).
 */
import type { WindSample } from './index';

/**
 * Re-exported from the catalogue, which is the one place model ids are defined.
 * There is no lookup table any more: the id we hold IS the id Open-Meteo wants.
 * The table that used to sit here mapped three short names onto Open-Meteo ids,
 * and the gap between the two vocabularies is what let a cast put an id the table
 * did not know about into the request, which then went out as `models=undefined`.
 */
import type { RealModelId } from './models';
export type { RealModelId };

export const OPEN_METEO_ATTRIBUTION = 'Weather data by Open-Meteo.com (CC BY 4.0)';

/** A grid of [lon, lat] points across [w, s, e, n], for the barb field. */
export function windGrid(
  bounds: [number, number, number, number],
  cols = 8,
  rows = 6,
): [number, number][] {
  const [w, s, e, n] = bounds;
  const out: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lon = w + ((e - w) * c) / (cols - 1);
      const lat = s + ((n - s) * r) / (rows - 1);
      out.push([lon, lat]);
    }
  }
  return out;
}

/** Build the Open-Meteo request: hourly 10 m wind (knots, UTC) for each point. */
export function buildWindUrl(
  points: [number, number][],
  omModel: string,
  extras: string[] = [],
  days = 3,
): string {
  const lat = points.map((p) => p[1].toFixed(4)).join(',');
  const lon = points.map((p) => p[0].toFixed(4)).join(',');
  return (
    'https://api.open-meteo.com/v1/forecast' +
    `?latitude=${lat}&longitude=${lon}` +
    // ONLY WHAT THE BARBS NEED. Gusts and pressure were added here on the
    // reasoning that they ride along for free. They do not: Open-Meteo rejects
    // the WHOLE request when a model does not carry a variable, so one extra
    // field turns a working model into a blank chart. That is what killed the
    // overlay on two of the seven models while GFS carried on fine, and it fails
    // silently, which is the worst way for it to fail. The point forecast asks
    // for the extras separately, where losing them costs only the extras.
    `&hourly=${['wind_speed_10m', 'wind_direction_10m', ...extras].join(',')}` +
    `&wind_speed_unit=kn&timezone=UTC&forecast_days=${days}` +
    `&models=${omModel}`
  );
}

/** The whole fetched field: per point, an hourly series of wind, on a shared time grid. */
export interface RealWindField {
  points: [number, number][];
  /** Hourly timestamps (UTC ms), shared across all points. */
  timesMs: number[];
  /** [pointIndex][hourIndex] wind speed, knots. */
  speedKn: number[][];
  /** [pointIndex][hourIndex] direction the wind comes FROM, degrees. */
  dirFromDeg: number[][];
  /** [pointIndex][hourIndex] gust speed, knots. NaN where not forecast. */
  gustKn: number[][];
  /** [pointIndex][hourIndex] mean sea-level pressure, hPa. NaN where not forecast. */
  pressureHpa: number[][];
}

interface HourlyLoc {
  hourly?: {
    time?: string[];
    wind_speed_10m?: number[];
    wind_direction_10m?: number[];
    wind_gusts_10m?: number[];
    pressure_msl?: number[];
  };
}

export function parseWindField(json: unknown, points: [number, number][]): RealWindField {
  const arr = (Array.isArray(json) ? json : [json]) as HourlyLoc[];
  let timesMs: number[] = [];
  const speedKn: number[][] = [];
  const dirFromDeg: number[][] = [];
  const gustKn: number[][] = [];
  const pressureHpa: number[][] = [];
  // Number(null) is 0, and a gust of 0 kn or a pressure of 0 hPa are not answers,
  // they are the absence of one. NaN, so a reader can tell the two apart.
  const nums = (a: unknown): number[] =>
    Array.isArray(a) ? a.map((v) => (v == null ? NaN : Number(v))) : [];
  for (let i = 0; i < points.length; i++) {
    const h = arr[i]?.hourly;
    if (h?.time && !timesMs.length) timesMs = h.time.map((t) => Date.parse(`${t}Z`));
    speedKn.push((h?.wind_speed_10m ?? []).map(Number));
    dirFromDeg.push((h?.wind_direction_10m ?? []).map(Number));
    gustKn.push(nums(h?.wind_gusts_10m));
    pressureHpa.push(nums(h?.pressure_msl));
  }
  return { points, timesMs, speedKn, dirFromDeg, gustKn, pressureHpa };
}

function nearestHourIndex(timesMs: number[], ms: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < timesMs.length; i++) {
    const d = Math.abs(timesMs[i] - ms);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function nearestPointIndex(points: [number, number][], pos: [number, number]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < points.length; i++) {
    const dx = points[i][0] - pos[0];
    const dy = points[i][1] - pos[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Wind at a position and instant: nearest grid point, nearest forecast hour. */
export function windAtTime(
  field: RealWindField,
  pos: [number, number],
  ms: number,
): { dirFromDeg: number; speedKn: number } {
  if (!field.points.length || !field.timesMs.length) return { dirFromDeg: 0, speedKn: 0 };
  const p = nearestPointIndex(field.points, pos);
  const t = nearestHourIndex(field.timesMs, ms);
  const speedKn = Number(field.speedKn[p]?.[t]);
  const dirFromDeg = Number(field.dirFromDeg[p]?.[t]);
  return {
    speedKn: Number.isFinite(speedKn) ? speedKn : 0,
    dirFromDeg: Number.isFinite(dirFromDeg) ? dirFromDeg : 0,
  };
}

/** The barb snapshot at one instant (every grid point's wind at that hour). */
export function fieldSamplesAt(field: RealWindField, ms: number): WindSample[] {
  const t = nearestHourIndex(field.timesMs, ms);
  if (t < 0) return [];
  const out: WindSample[] = [];
  for (let i = 0; i < field.points.length; i++) {
    const speedKn = Number(field.speedKn[i]?.[t]);
    const dirFromDeg = Number(field.dirFromDeg[i]?.[t]);
    if (Number.isFinite(speedKn) && Number.isFinite(dirFromDeg)) {
      out.push({ position: field.points[i], dirFromDeg, speedKn });
    }
  }
  return out;
}

type FetchLike = (url: string) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/** Fetch the whole hourly wind field over `bounds` for `modelId`. Throws on failure. */
export async function fetchRealWindField(
  bounds: [number, number, number, number],
  modelId: RealModelId,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
  days = 3,
): Promise<RealWindField> {
  const points = windGrid(bounds);
  const url = buildWindUrl(points, modelId, [], days);
  const r = await fetchImpl(url);
  if (!r.ok) throw new Error(`Weather fetch failed (${r.status})`);
  return parseWindField(await r.json(), points);
}
